import React, { useState, useEffect } from 'react';
import { Activity } from '../../../../types/Activity';
import { TaskStatus, TaskSubType, TaskSubTypeDisplayText } from '../../../../types/RoutineTask';
import { activityService } from '../../../../services/activityService';
import { routineTaskService } from '../../../../services/routineTaskService';
import { standingTaskService } from '../../../../services/standingTaskService';
import { RoutineTask } from '../../../../types/RoutineTask';
import type { CreateStandingRoutineTaskDTO } from '../../../../services/routineTaskService';
import type { CreateRoutineTaskDTO } from '../../../../services/standingTaskService';
import { usePermissions } from '../../../../hooks/usePermissions';
import ResponsiveModal from '../../../../components/ResponsiveModal';
import { SearchableDropdown } from '../../../../components/SearchableDropdown';
import { Alert, Button, Form, InputGroup, Card, Row, Col, Tabs, Tab } from '@/components/ui';


interface RoutineTaskModalProps {
  open: boolean;
  task?: RoutineTask | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const RoutineTaskModal: React.FC<RoutineTaskModalProps> = ({
  open,
  task,
  onCancel,
  onSuccess,
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [allocationErrors, setAllocationErrors] = useState<{
    labour: boolean;
    material: boolean;
    equipment: boolean;
  }>({
    labour: false,
    material: false,
    equipment: false
  });
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    status: TaskStatus.Active,
    taskSubType: '',
    primaryActivityId: '',
    secondaryActivityId: '',
    primaryLabourAllocation: 100,
    primaryMaterialAllocation: 100,
    primaryEquipmentAllocation: 100,
    secondaryLabourAllocation: 0,
    secondaryMaterialAllocation: 0,
    secondaryEquipmentAllocation: 0
  });
  const [taskNumber, setTaskNumber] = useState('');
  const { hasPermission } = usePermissions();
  const isReadOnly = false; // Removed standing routine permission check

  // Validation function to check if allocations sum to 100%
  const validateAllocations = (primary: number, secondary: number): boolean => {
    return (primary + secondary) === 100;
  };

  // Update allocation errors when form data changes
  const updateAllocationErrors = (newFormData: typeof formData) => {
    const hasSecondary = !!newFormData.secondaryActivityId;
    
    // Only validate if there's a secondary activity
    if (hasSecondary) {
      setAllocationErrors({
        labour: !validateAllocations(newFormData.primaryLabourAllocation, newFormData.secondaryLabourAllocation),
        material: !validateAllocations(newFormData.primaryMaterialAllocation, newFormData.secondaryMaterialAllocation),
        equipment: !validateAllocations(newFormData.primaryEquipmentAllocation, newFormData.secondaryEquipmentAllocation)
      });
    } else {
      // Clear errors when no secondary activity
      setAllocationErrors({
        labour: false,
        material: false,
        equipment: false
      });
    }
  };

  // Handle form data changes with validation
  const handleFormDataChange = (updates: Partial<typeof formData>) => {
    const newFormData = { ...formData, ...updates };
    
    // Handle secondary activity changes
    if (updates.secondaryActivityId !== undefined) {
      if (!updates.secondaryActivityId) {
        // Secondary activity removed - set primary to 100, secondary to 0
        newFormData.primaryLabourAllocation = 100;
        newFormData.primaryMaterialAllocation = 100;
        newFormData.primaryEquipmentAllocation = 100;
        newFormData.secondaryLabourAllocation = 0;
        newFormData.secondaryMaterialAllocation = 0;
        newFormData.secondaryEquipmentAllocation = 0;
      } else {
        // Secondary activity added - reset secondary allocations to 0
        newFormData.secondaryLabourAllocation = 0;
        newFormData.secondaryMaterialAllocation = 0;
        newFormData.secondaryEquipmentAllocation = 0;
      }
    }
    
    setFormData(newFormData);
    updateAllocationErrors(newFormData);
  };

  // Handle ID changes and auto-populate task number
  const handleIdChange = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    
    // Update form data directly without triggering allocation validation
    setFormData(prev => ({ ...prev, id: numericValue }));
    
    // Auto-populate task number with R prefix
    if (numericValue) {
      setTaskNumber(`R${numericValue}`);
    } else {
      setTaskNumber('');
    }
  };


  useEffect(() => {
    const loadActivities = async () => {
      try {
        const data = await activityService.getActivitiesByClassification('routine');
        setActivities(data);
      } catch (error) {
        console.error('Failed to load activities:', error);
      }
    };

    if (open) {
      loadActivities();
    }
  }, [open]);

  useEffect(() => {
    if (task && open) {
      setFormData({
        id: task.id?.toString() || '',
        name: task.name || '',
        description: task.description || '',
        status: task.status as TaskStatus,
        taskSubType: task.taskSubType || '',
        primaryActivityId: task.activities && task.activities.length > 0 ? task.activities[0].id.toString() : '',
        secondaryActivityId: task.activities && task.activities.length > 1 ? task.activities[1].id.toString() : '',
        primaryLabourAllocation: task.labourAllocation || 0,
        primaryMaterialAllocation: task.materialAllocation || 0,
        primaryEquipmentAllocation: task.equipmentAllocation || 0,
        secondaryLabourAllocation: task.secondaryLabourAllocation || 0,
        secondaryMaterialAllocation: task.secondaryMaterialAllocation || 0,
        secondaryEquipmentAllocation: task.secondaryEquipmentAllocation || 0
      });
      // Set task number for existing task
      if (task.taskNumber) {
        setTaskNumber(task.taskNumber);
      } else if (task.id) {
        setTaskNumber(`R${task.id}`);
      }
    } else if (!open) {
      setFormData({
        id: '',
        name: '',
        description: '',
        status: TaskStatus.Active,
        taskSubType: '',
        primaryActivityId: '',
        secondaryActivityId: '',
        primaryLabourAllocation: 100,
        primaryMaterialAllocation: 100,
        primaryEquipmentAllocation: 100,
        secondaryLabourAllocation: 0,
        secondaryMaterialAllocation: 0,
        secondaryEquipmentAllocation: 0
      });
      setTaskNumber('');
    }
  }, [task, open]);

  const handleSave = async () => {
    if (isReadOnly) return;
    
    // Validate allocations before saving (only if secondary activity is selected)
    const hasSecondary = !!formData.secondaryActivityId;
    const hasAllocationErrors = hasSecondary && (
      !validateAllocations(formData.primaryLabourAllocation, formData.secondaryLabourAllocation) ||
      !validateAllocations(formData.primaryMaterialAllocation, formData.secondaryMaterialAllocation) ||
      !validateAllocations(formData.primaryEquipmentAllocation, formData.secondaryEquipmentAllocation)
    );

    if (hasAllocationErrors) {
      setError('Primary and secondary activity allocations must sum to 100% for each category (Labour, Material, Equipment)');
      return;
    }
    
    try {
      const payload: CreateRoutineTaskDTO = {
        id: formData.id ? parseInt(formData.id) : undefined,
        name: formData.name,
        description: formData.description,
        activityId: parseInt(formData.primaryActivityId),
        secondaryActivityId: formData.secondaryActivityId ? parseInt(formData.secondaryActivityId) : undefined,
        status: formData.status,
        type: 'Routine',
        taskSubType: formData.taskSubType ? formData.taskSubType as TaskSubType : undefined,
        labourAllocation: formData.primaryLabourAllocation,
        materialAllocation: formData.primaryMaterialAllocation,
        equipmentAllocation: formData.primaryEquipmentAllocation,
        ...(formData.secondaryActivityId && {
          secondaryLabourAllocation: formData.secondaryLabourAllocation,
          secondaryMaterialAllocation: formData.secondaryMaterialAllocation,
          secondaryEquipmentAllocation: formData.secondaryEquipmentAllocation
        })
      };

      if (task) {
        // For updates, use the original service and payload structure
        const updatePayload: CreateStandingRoutineTaskDTO = {
          id: formData.id ? parseInt(formData.id) : undefined,
          name: formData.name,
          description: formData.description,
          activities: [parseInt(formData.primaryActivityId), ...(formData.secondaryActivityId ? [parseInt(formData.secondaryActivityId)] : [])],
          status: formData.status,
          mode: 'standing',
          type: 'Routine',
          taskSubType: formData.taskSubType ? formData.taskSubType as TaskSubType : undefined,
          labourAllocation: formData.primaryLabourAllocation,
          materialAllocation: formData.primaryMaterialAllocation,
          equipmentAllocation: formData.primaryEquipmentAllocation,
          ...(formData.secondaryActivityId && {
            secondaryLabourAllocation: formData.secondaryLabourAllocation,
            secondaryMaterialAllocation: formData.secondaryMaterialAllocation,
            secondaryEquipmentAllocation: formData.secondaryEquipmentAllocation
          })
        };
        await routineTaskService.update(task.id, updatePayload);
      } else {
        await standingTaskService.createRoutineTask(payload);
      }
      
      setError(null);
      onSuccess();
    } catch (error) {
      console.error(`Failed to ${task ? 'update' : 'create'} routine task:`, error);
      setError(`Failed to ${task ? 'update' : 'create'} routine task`);
    }
  };

  const modalFooter = (
    <div className="tw-flex tw-gap-2">
      <Button
        type="button"
        variant="secondary"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={handleSave}
        disabled={isReadOnly || (!!formData.secondaryActivityId && (
          allocationErrors.labour || allocationErrors.material || allocationErrors.equipment
        ))}
      >
        {task ? 'Update' : 'Create'}
      </Button>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onCancel}
      title={task ? 'Edit Routine Task' : 'Create Routine Task'}
      footer={modalFooter}
      size="large"
    >
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}
      <Tabs defaultActiveKey="basic" className="tw-mb-3">
              <Tab eventKey="basic" title="Basic Info">
                <form>
                  <div className="tw-mb-3">
                    <Form.Label>ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.id}
                      onChange={(e) => handleIdChange(e.target.value)}
                      placeholder="Enter task ID (numbers only)"
                    />
                  </div>
                  <div className="tw-mb-3">
                    <Form.Label>Task Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={taskNumber}
                      readOnly
                      style={{ backgroundColor: '#e9ecef', color: '#6c757d' }}
                      placeholder="Task number will auto-populate"
                    />
                  </div>
                  <div className="tw-mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormDataChange({name: e.target.value})}
                      placeholder="Enter task name"
                      required
                    />
                  </div>
                  <div className="tw-mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      value={formData.description}
                      onChange={(e) => handleFormDataChange({description: e.target.value})}
                      placeholder="Enter task description"
                      rows={3}
                    />
                  </div>
                  <div className="tw-mb-3">
                    <SearchableDropdown
                      label="Task Subtype"
                      options={Object.values(TaskSubType)}
                      value={formData.taskSubType}
                      onChange={(value) => handleFormDataChange({taskSubType: value})}
                      formatOption={(subtype) => TaskSubTypeDisplayText[subtype]}
                      getValue={(subtype) => subtype}
                      placeholder="Select Task Subtype"
                      noOptionsMessage="No subtypes available"
                    />
                  </div>
                  <div className="tw-mb-3">
                    <SearchableDropdown
                      label="Status"
                      options={Object.values(TaskStatus)}
                      value={formData.status}
                      onChange={(value) => handleFormDataChange({status: value as TaskStatus})}
                      formatOption={(status) => status}
                      getValue={(status) => status}
                      placeholder="Select Status"
                      noOptionsMessage="No statuses available"
                    />
                  </div>
                </form>
              </Tab>
              <Tab eventKey="activity" title="Activity Details">
                <form>
                  <Row>
                    {/* Primary Activity Column */}
                    <Col md={6}>
                      <h6 className="tw-text-primary tw-mb-3">Primary Activity</h6>
                      <div className="tw-mb-3">
                        <SearchableDropdown
                          label="Activity"
                          options={activities}
                          value={formData.primaryActivityId}
                          onChange={(value) => handleFormDataChange({primaryActivityId: value})}
                          formatOption={(activity) => `${activity.activityName} (#${activity.activityNumber})`}
                          getValue={(activity) => activity.id.toString()}
                          placeholder="Select Primary Activity"
                          noOptionsMessage="No activities available"
                          isRequired
                        />
                      </div>
                      
                      <div className="tw-mb-3">
                        <Form.Label>Labour Allocation</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            isInvalid={allocationErrors.labour}
                            value={formData.primaryLabourAllocation}
                            onChange={(e) => {
                              const value = Math.max(0, parseFloat(e.target.value) || 0);
                              handleFormDataChange({primaryLabourAllocation: value});
                            }}
                            readOnly={!formData.secondaryActivityId}
                            style={!formData.secondaryActivityId ? { backgroundColor: '#e9ecef', color: '#6c757d' } : {}}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        {allocationErrors.labour && (
                          <Form.Control.Feedback type="invalid">
                            Primary and secondary labour allocations must sum to 100%
                          </Form.Control.Feedback>
                        )}
                      </div>

                      <div className="tw-mb-3">
                        <Form.Label>Material Allocation</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            isInvalid={allocationErrors.material}
                            value={formData.primaryMaterialAllocation}
                            onChange={(e) => {
                              const value = Math.max(0, parseFloat(e.target.value) || 0);
                              handleFormDataChange({primaryMaterialAllocation: value});
                            }}
                            readOnly={!formData.secondaryActivityId}
                            style={!formData.secondaryActivityId ? { backgroundColor: '#e9ecef', color: '#6c757d' } : {}}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        {allocationErrors.material && (
                          <Form.Control.Feedback type="invalid">
                            Primary and secondary material allocations must sum to 100%
                          </Form.Control.Feedback>
                        )}
                      </div>

                      <div className="tw-mb-3">
                        <Form.Label>Equipment Allocation</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            isInvalid={allocationErrors.equipment}
                            value={formData.primaryEquipmentAllocation}
                            onChange={(e) => {
                              const value = Math.max(0, parseFloat(e.target.value) || 0);
                              handleFormDataChange({primaryEquipmentAllocation: value});
                            }}
                            readOnly={!formData.secondaryActivityId}
                            style={!formData.secondaryActivityId ? { backgroundColor: '#e9ecef', color: '#6c757d' } : {}}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        {allocationErrors.equipment && (
                          <Form.Control.Feedback type="invalid">
                            Primary and secondary equipment allocations must sum to 100%
                          </Form.Control.Feedback>
                        )}
                      </div>
                    </Col>

                    {/* Secondary Activity Column */}
                    <Col md={6}>
                      <h6 className="tw-text-content-secondary tw-mb-3">Secondary Activity</h6>
                      <div className="tw-mb-3">
                        <SearchableDropdown
                          label="Activity"
                          options={activities}
                          value={formData.secondaryActivityId}
                          onChange={(value) => handleFormDataChange({secondaryActivityId: value})}
                          formatOption={(activity) => `${activity.activityName} (#${activity.activityNumber})`}
                          getValue={(activity) => activity.id.toString()}
                          placeholder="Select Secondary Activity"
                          noOptionsMessage="No activities available"
                          allowClear={true}
                        />
                      </div>
                      
                      {formData.secondaryActivityId && (
                        <>
                          <div className="tw-mb-3">
                            <Form.Label>Labour Allocation</Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                isInvalid={allocationErrors.labour}
                                value={formData.secondaryLabourAllocation}
                                onChange={(e) => {
                                  const value = Math.max(0, parseFloat(e.target.value) || 0);
                                  handleFormDataChange({secondaryLabourAllocation: value});
                                }}
                              />
                              <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup>
                            {allocationErrors.labour && (
                              <Form.Control.Feedback type="invalid">
                                Primary and secondary labour allocations must sum to 100%
                              </Form.Control.Feedback>
                            )}
                          </div>

                          <div className="tw-mb-3">
                            <Form.Label>Material Allocation</Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                isInvalid={allocationErrors.material}
                                value={formData.secondaryMaterialAllocation}
                                onChange={(e) => {
                                  const value = Math.max(0, parseFloat(e.target.value) || 0);
                                  handleFormDataChange({secondaryMaterialAllocation: value});
                                }}
                              />
                              <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup>
                            {allocationErrors.material && (
                              <Form.Control.Feedback type="invalid">
                                Primary and secondary material allocations must sum to 100%
                              </Form.Control.Feedback>
                            )}
                          </div>

                          <div className="tw-mb-3">
                            <Form.Label>Equipment Allocation</Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                isInvalid={allocationErrors.equipment}
                                value={formData.secondaryEquipmentAllocation}
                                onChange={(e) => {
                                  const value = Math.max(0, parseFloat(e.target.value) || 0);
                                  handleFormDataChange({secondaryEquipmentAllocation: value});
                                }}
                              />
                              <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup>
                            {allocationErrors.equipment && (
                              <Form.Control.Feedback type="invalid">
                                Primary and secondary equipment allocations must sum to 100%
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>

                  {/* Allocation Summary - Only show when secondary activity is selected */}
                  {formData.secondaryActivityId && (
                    <Card className="tw-mt-4">
                      <Card.Header>
                        <h6 className="tw-mb-0">Allocation Summary</h6>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <div className={`tw-text-center tw-p-2 tw-rounded ${allocationErrors.labour ? 'tw-bg-danger tw-text-white' : 'tw-bg-success tw-text-white'}`}>
                              <strong>Labour: {formData.primaryLabourAllocation + formData.secondaryLabourAllocation}%</strong>
                              {allocationErrors.labour && <div className="tw-text-sm">Must equal 100%</div>}
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className={`tw-text-center tw-p-2 tw-rounded ${allocationErrors.material ? 'tw-bg-danger tw-text-white' : 'tw-bg-success tw-text-white'}`}>
                              <strong>Material: {formData.primaryMaterialAllocation + formData.secondaryMaterialAllocation}%</strong>
                              {allocationErrors.material && <div className="tw-text-sm">Must equal 100%</div>}
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className={`tw-text-center tw-p-2 tw-rounded ${allocationErrors.equipment ? 'tw-bg-danger tw-text-white' : 'tw-bg-success tw-text-white'}`}>
                              <strong>Equipment: {formData.primaryEquipmentAllocation + formData.secondaryEquipmentAllocation}%</strong>
                              {allocationErrors.equipment && <div className="tw-text-sm">Must equal 100%</div>}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}
                </form>
              </Tab>
            </Tabs>
    </ResponsiveModal>
  );
};

export default RoutineTaskModal; 